import type {
  Coassign_User_Set_Input,
  MutateUpdateFirebaseUserMutationVariables
} from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import type { User, UserWithRelations } from '../user'
import {
  updateUserMutationDocument,
  insertUserToDepartmentMutationDocument,
  insertUserToJobTitleMutationDocument,
  insertUserToJobSkillMutationDocument,
  updateUserToDepartmentMutationDocument,
  updateUserToJobTitleMutationDocument,
  updateUserToJobSkillMutationDocument,
  mutateUpdateFirebaseUser
} from './userMutations'

import { buildM2MMutations } from '@/lib/utils/m2mHelper'

export function changeUserMutationQuery(
  id: string,
  modifiedUser: Partial<User>,
  prev: UserWithRelations,
  firebaseTenantId: string,
  commitId: string
): MutationOperationAndVariables[] {
  if (!id || !firebaseTenantId) throw new Error('add user required parameter id not found')
  const mutations: MutationOperationAndVariables[] = []
  mutations.push(buildChangeMutationForUserDocument(id, modifiedUser, commitId))
  if (modifiedUser.departmentIds) {
    mutations.push(
      ...buildDepartmentMutations(
        id,
        prev.departmentIds || [],
        modifiedUser.departmentIds,
        commitId
      )
    )
  }
  if (modifiedUser.jobTitleIds) {
    mutations.push(
      ...buildJobTitleMutations(id, prev.jobTitleIds || [], modifiedUser.jobTitleIds, commitId)
    )
  }
  if (modifiedUser.jobSkillIds) {
    mutations.push(
      ...buildJobSkillMutations(id, prev.jobSkillIds || [], modifiedUser.jobSkillIds, commitId)
    )
  }
  const firebaseUid = prev.firebase_uid
  if (
    (modifiedUser.email != undefined && modifiedUser.email != prev.email) ||
    (modifiedUser.available != undefined && modifiedUser.available != prev.available)
  ) {
    const email = modifiedUser.email === undefined ? prev.email : modifiedUser.email
    const disabled = modifiedUser.available === undefined ? undefined : !modifiedUser.available
    // TODO:ダミーデータではなくなりFirebaseアカウントが必ず作成されるようになったらチェックをちゃんとする
    // if (!firebaseUid) throw new Error('modified email required firebase uid not found')
    if (firebaseUid !== null) {
      mutations.push(
        buildChangeMutationForFirebaseDocument(email, firebaseTenantId, firebaseUid, disabled)
      )
    }
  }
  return mutations
}

function buildChangeMutationForUserDocument(
  id: string,
  user: Partial<User>,
  commitId: string
): MutationOperationAndVariables {
  const variables: Coassign_User_Set_Input = {
    commit_id: commitId,
    name: user.name,
    role: user.role,
    email: user.email,
    note: user.note,
    available: user.available,
    display_order: user.display_order
  }
  return { mutation: updateUserMutationDocument, variables: { id: id, input: variables } }
}

function buildDepartmentMutations(
  userId: string,
  base_departments: string[],
  departments: string[],
  commitId: string
): MutationOperationAndVariables[] {
  return buildM2MMutations({
    fromColumn: 'user_id',
    fromId: userId,
    toColumn: 'department_id',
    beforeToIds: base_departments,
    afterToIds: departments,
    updateQuery: updateUserToDepartmentMutationDocument,
    insertQuery: insertUserToDepartmentMutationDocument,
    commitId: commitId
  })
}

function buildJobTitleMutations(
  userId: string,
  base_job_titles: string[],
  job_titles: string[],
  commitId: string
): MutationOperationAndVariables[] {
  return buildM2MMutations({
    fromColumn: 'user_id',
    fromId: userId,
    toColumn: 'job_title_id',
    beforeToIds: base_job_titles,
    afterToIds: job_titles,
    updateQuery: updateUserToJobTitleMutationDocument,
    insertQuery: insertUserToJobTitleMutationDocument,
    commitId: commitId
  })
}

function buildJobSkillMutations(
  userId: string,
  base_job_skills: string[],
  job_skills: string[],
  commitId: string
): MutationOperationAndVariables[] {
  return buildM2MMutations({
    fromColumn: 'user_id',
    fromId: userId,
    toColumn: 'job_skill_id',
    beforeToIds: base_job_skills,
    afterToIds: job_skills,
    updateQuery: updateUserToJobSkillMutationDocument,
    insertQuery: insertUserToJobSkillMutationDocument,
    commitId: commitId
  })
}

function buildChangeMutationForFirebaseDocument(
  email: string,
  firebase_tenant_id: string,
  firebase_uid: string,
  disabled: boolean | undefined
): MutationOperationAndVariables {
  if (!firebase_uid) throw new Error('update firebase required firebase_uid')
  const variables: MutateUpdateFirebaseUserMutationVariables = {
    firebase_tenant_id: firebase_tenant_id,
    firebase_uid: firebase_uid,
    email: email,
    disabled: disabled
  }
  return {
    mutation: mutateUpdateFirebaseUser,
    variables: variables
  }
}
